import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'query-string';

import Spinner from '../../../../../../components/global/Spinner';
import AlbumSlideshowGridViewer from '../../../../../../components/grid/components/AlbumSlideshowGridViewer';
import useBasename from '../../../../../../components/routing/useBasename';
import AlbumSlideshowViewerActions from '../../../../components/AlbumSlideshowViewerActions';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import AlbumSlideshowShareModal, {
  ALBUM_SLIDESHOW_SHARE_MODAL,
} from '../../../../../../components/global/AlbumSlideshowShareModal';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { DISPLAY_MODE } from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/AlbumSlideshowGridViewer';
import useAlbumSlideshow from '../hooks/useAlbumSlideshow';
import AlbumSlideshowOnboardingModal, {
  ALBUM_SLIDESHOW_ONBOARDING_MODAL,
} from '../../../../../../components/global/AlbumSlideshowOnboardingModal';
import { useContributorPermission } from '../../../../services/contributors';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../constants/albums';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowViewerDismiss,
  postNativeAppAlbumSlideshowViewerUpdateError,
  postNativeAppAlbumSlideshowViewerShareDialog,
} from '../../../../../../services/native-bridge/native-apps-interface';
import { onFullScreenToggle } from '../../../../../../util/helpers';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slideId: PropTypes.string,
    }),
  }),
  slideshow: PropTypes.shape({
    photos: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
    slide_duration_ms: PropTypes.number,
  }),
};

const AlbumSlideshowView = ({ history, location, match }) => {
  const basename = useBasename();
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.PREVIEW);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const isNativeView = isNativeAppWebView();
  const queryParams = qs.parse(location.search);
  const { albumId, slideId } = match.params;

  const {
    album,
    slideshow,
    loading,
    loadingMore,
    onLoadMore,
    onSlideshowUpdate,
    onSlideshowDelete,
  } = useAlbumSlideshow({
    albumId,
  });

  const { isOwner, isContributor } = useContributorPermission({
    entityId: albumId,
  });

  useEffect(() => {
    if (queryParams?.present === '1') {
      setDisplayMode(DISPLAY_MODE.PRESENT);
    }

    if (queryParams?.contribute === '1') {
      openModal(ALBUM_SLIDESHOW_ONBOARDING_MODAL);
    }
  }, []);

  if (!slideshow) {
    return (
      <div className="w-100 h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  if (!slideId && slideshow.intro_asset_image) {
    history.replace(
      `${location.pathname}/s/${slideshow.intro_asset_image.id}?present=${
        queryParams?.present ?? 0
      }`,
    );
  } else if (!slideId && slideshow.photos.total > 0) {
    history.replace(
      `${location.pathname}/s/${slideshow.photos.records[0].photo.id}?present=${
        queryParams?.present ?? 0
      }`,
    );
  }

  const onDelete = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      await onSlideshowDelete();
      if (isNativeView) {
        postNativeAppAlbumSlideshowViewerDismiss();
      } else {
        history.push(`/app/${basename}/albums/${albumId}/photos`);
      }
    } catch (err) {
      const errorString = getErrorString(err);
      if (isNativeView) {
        postNativeAppAlbumSlideshowViewerUpdateError(errorString);
      } else {
        openAlert({
          message: 'There was an error updating the slideshow.',
          info: errorString,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const onDeletePhoto = async ({ photoId, isAssetImage }) => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      // IsAssetImage will only really true for the intro slide.
      // therefore, we can safely rely on it to remove the intro slide.
      await onSlideshowUpdate({
        createExcludedPhotos:
          !isAssetImage && slideshow.mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
            ? [{ photo_id: photoId }]
            : undefined,
        deleteSelectedPhotos:
          !isAssetImage &&
          slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
            ? [{ photo_id: photoId }]
            : undefined,
        introAssetImage: isAssetImage ? null : undefined,
      });
    } catch (err) {
      const errorString = getErrorString(err);
      if (isNativeView) {
        postNativeAppAlbumSlideshowViewerUpdateError(errorString);
      } else {
        openAlert({
          message: 'There was an error updating the slideshow.',
          info: errorString,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const onPublishToggle = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      await onSlideshowUpdate({
        status:
          slideshow.status === ALBUM_SLIDESHOW_STATUS.DRAFT
            ? ALBUM_SLIDESHOW_STATUS.PUBLISHED
            : ALBUM_SLIDESHOW_STATUS.DRAFT,
      });
    } catch (err) {
      const errorString = getErrorString(err);
      if (isNativeView) {
        postNativeAppAlbumSlideshowViewerUpdateError(errorString);
      } else {
        openAlert({
          message: 'There was an error updating the slideshow.',
          info: errorString,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <AlbumSlideshowGridViewer
        album={album}
        saving={saving}
        loading={loading}
        slideshow={slideshow}
        recordId={match.params.slideId}
        records={
          slideshow.intro_asset_image
            ? [
                { asset_image: slideshow.intro_asset_image },
                ...slideshow.photos.records,
              ]
            : slideshow.photos.records
        }
        recordsTotal={slideshow.photos.total}
        loadMore={onLoadMore}
        loadingMore={loadingMore}
        shouldLoadMore={
          slideshow.photos.total > slideshow.photos.records.length
        }
        gridPath={`/app/${basename}/albums/${albumId}/photos`}
        displayMode={displayMode}
        onDeletePhoto={authenticated && isOwner ? onDeletePhoto : undefined}
        dismissible={
          displayMode !== DISPLAY_MODE.PRESENT &&
          ((authenticated && !!basename) || isNativeView)
        }
        onDismiss={() => {
          if (isNativeView) {
            postNativeAppAlbumSlideshowViewerDismiss();
          } else {
            history.goBack();
          }
        }}
        renderTitle={() => {
          if (isNativeView) {
            return '';
          }
          return (
            <Fragment>
              <h3 className="w-100 text-white text-center mb-0 d-none d-md-block">
                {album?.name}
              </h3>
              <h5 className="w-100 text-white text-center mb-0 d-md-none">
                {album?.name}
              </h5>
            </Fragment>
          );
        }}
        actions={({ playbackActions }) => (
          <AlbumSlideshowViewerActions
            isOwner={isOwner}
            isContributor={isContributor}
            authenticated={authenticated || isNativeView}
            onEdit={
              displayMode === DISPLAY_MODE.PRESENT
                ? undefined
                : () => {
                    // Stop the playback if possible
                    playbackActions?.onPlaybackStop?.();
                    history.push(
                      `/app/${basename}/albums/${albumId}/slideshow/designer`,
                    );
                  }
            }
            onPresent={
              displayMode === DISPLAY_MODE.PRESENT
                ? undefined
                : () => setDisplayMode(DISPLAY_MODE.PRESENT)
            }
            onShare={
              displayMode === DISPLAY_MODE.PRESENT ||
              slideshow.status === ALBUM_SLIDESHOW_STATUS.DRAFT
                ? undefined
                : () => {
                    // Stop the playback if possible
                    playbackActions?.onPlaybackStop?.();
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowViewerShareDialog(albumId);
                    } else {
                      openModal(ALBUM_SLIDESHOW_SHARE_MODAL);
                    }
                  }
            }
            onAddPhotos={
              displayMode === DISPLAY_MODE.PRESENT || isNativeView
                ? undefined
                : () => {
                    // Stop the playback if possible
                    playbackActions?.onPlaybackStop?.();
                    openModal(ALBUM_SLIDESHOW_ONBOARDING_MODAL);
                  }
            }
            onDelete={
              displayMode === DISPLAY_MODE.PRESENT ? undefined : onDelete
            }
            onPublishToggle={
              displayMode === DISPLAY_MODE.PRESENT ? undefined : onPublishToggle
            }
            onFullScreenToggle={
              displayMode === DISPLAY_MODE.PRESENT || isNativeView
                ? undefined
                : () => onFullScreenToggle(document.documentElement)
            }
            isPublished={slideshow.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED}
            loading={saving}
          />
        )}
      />
      <AlbumSlideshowShareModal album={album} albumId={albumId} />
      <AlbumSlideshowOnboardingModal albumId={albumId} />
    </Fragment>
  );
};

AlbumSlideshowView.propTypes = propTypes;

export default compose(withRouter)(AlbumSlideshowView);
